/* eslint-disable arrow-body-style */
/*
  Module configuration file
 */
import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {ModuleConfiguration} from "../core/config/module-configuration";
import {TopicConfiguration} from "./topic-configuration";
import {FeatureLayerConfiguration} from "../core/config/layer-configuration";
import {PopupConfiguration} from "../core/config/popup-configuration";
import {LegendConfiguration} from "./legend-configuration";
import {SidebarConfiguration} from "./sidebar-configuration";
import {LayerListConfiguration} from "../core/config/layer-list-configuration";
import {WebMapConfiguration} from "../core/config/web-map-configuration";
import {
    FillSymbolConfiguration,
    PictureMarkerSymbolConfiguration, SymbolConfiguration
} from "../core/config/symbol-configuration";
import {MaintenanceConfiguration} from "../core/config/maintenance-configuration";
import {NavigationConfiguration} from "../config/navigation-configuration";
import {PrintConfiguration} from "../core/config/print-configuration";
import {AuftragsverwaltungApiConfiguration} from "./auftragsverwaltung-api-configuration";
import {ViewManagerConfiguration} from "../core/config/view-manager-configuration";
import {MessageConfiguration} from "../core/config/message-configuration";
import {LinkConfiguration} from "../core/config/link-configuration";
import {ToolsConfiguration} from "../core/config/tools-configuration";
import {DomainConfiguration, DomainType} from "../core/config/domain-configuration";
import {ProcessConfiguration} from "./process-configuration";
import {DataPortalConfiguration} from "./data-portal-configuration";
import {ProcessLogConfiguration} from "./process-log-configuration";




@JsonObject()
export class WebMapMetaConfiguration extends ModuleConfiguration {
    @JsonProperty()
    declare alerts: MessageConfiguration[];

    constructor() {
        super();
    }
}
@JsonObject()
export class TrackerConfiguration {
    @JsonProperty()
    declare urlBase: string;
    @JsonProperty()
    declare siteId: number;
    constructor() {
    }
}
@JsonObject()
export class MapViewerConfiguration extends ModuleConfiguration {
    @JsonProperty()
    declare breakpoints: {
        [key: string]: number;
    };
    // the topic activated on application's start
    @JsonProperty()
    declare initialTopic: string;

    /**
     * Layer list configuration
     */
    @JsonProperty()
    declare layerList: LayerListConfiguration;

    /**
     * Layer configurations (e.g. station layer)
     */
    @JsonProperty()
    declare layers: FeatureLayerConfiguration[];

    /**
     * Viewmanager
     */
    @JsonProperty()
    declare viewManager?: ViewManagerConfiguration;

    /**
     * "über diese Karte" Link
     */
    @JsonProperty()
    declare aboutLink?: LinkConfiguration;
    /**
     * legend
     */
    @JsonProperty()
    declare legend: LegendConfiguration;

    /**
     * Printing
     */
    @JsonProperty()
    declare print: PrintConfiguration;

    /**
     * Information about maintenance work on the application
     */
    @JsonProperty({ type: MaintenanceConfiguration})
    declare maintenance?: MaintenanceConfiguration[];

    // Objektabfrage Popup
    @JsonProperty()
    declare popup: PopupConfiguration;

    @JsonProperty()
    declare tools: ToolsConfiguration;

    // Topics (Themen)
    @JsonProperty()
    declare topics: TopicConfiguration[];

    @JsonProperty()
    declare symbols: HashMap<SymbolConfiguration>;

    // Objektabfrage Highlight
    @JsonProperty()
    declare highlightSymbol: FillSymbolConfiguration;

    @JsonProperty()
    declare pointerSymbols?: HashMap<PictureMarkerSymbolConfiguration>;

    @JsonProperty()
    declare sidebar: SidebarConfiguration;

    @JsonProperty()
    declare tracker?: TrackerConfiguration;

    @JsonProperty()
    declare urls?: any;

    @JsonProperty()
    declare navigation: NavigationConfiguration;
    @JsonProperty()
    declare breadcrumbs: NavigationConfiguration;

    @JsonProperty()
    declare webMap: WebMapConfiguration;

    @JsonProperty()
    declare meta: WebMapMetaConfiguration;

    @JsonProperty()
    declare auftragsverwaltungApi: AuftragsverwaltungApiConfiguration;

    @JsonProperty()
    declare htmlContent: HashMap<string | HashMap<string>>;

    @JsonProperty()
    declare adressen: HashMap<HashMap<string>>;
    /**
     * Enable or disable search field in locate button
     *
     * @default true
     */
    @JsonProperty() locateButtonEnabled = true;

    @JsonProperty({ type: DomainType} )
    declare domains: DomainConfiguration[];

    @JsonProperty()
    declare dataPortal: DataPortalConfiguration;

    @JsonProperty()
    declare processes: ProcessConfiguration[];

    @JsonProperty()
    declare processLog: ProcessLogConfiguration;

    constructor() {
        super();
    }

    /**
     * Get topic configuration by ID
     *
     * @param topicId
     */
    getTopic(topicId: string): TopicConfiguration | undefined {
        return this.topics.find((topic) => topic.id === topicId);
    }

    /**
     * Get suptopic
     *
     * @param topicId
     * @param subtopicId
     */
    getSubtopic(topicId: string, subtopicId: string): TopicConfiguration | undefined {
        const topics = this.getTopicsByParentId(topicId);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return topics.find((t: TopicConfiguration) => t.id === subtopicId);
    }

    /**
     * Get topics with specified id as parentId
     *
     * @param topicId
     */
    getTopicsByParentId(topicId: string): TopicConfiguration[] | undefined {
        return this.topics.filter((topic) => topic.parentId === topicId);
    }

    /**
     * Get topic configuration by route
     *
     * @param route
     */
    getTopicByRoute(route: string, topics: TopicConfiguration[] = []): TopicConfiguration | undefined {
        if(topics.length === 0) {
            topics = this.topics;
        }
        return topics.find((topic) => topic.routes && topic.routes.includes(route));
    }
    /**
     * Get topic configuration by route
     *
     * @param route
     */
    getDefaultTopic(): TopicConfiguration | undefined {
        return this.topics[0];
    }

    /**
     * Get first selectable subtopic (used when a parent topic is selected)
     *
     * @param topic
     * @param topics optional
     */
    getFirstSubtopic(topic: TopicConfiguration, topics: TopicConfiguration[] = []): TopicConfiguration {
        if (!this.topics.length && !topics.length) {
            throw new Error("No topics configured.");
        }
        if(topics.length === 0) {
            topics = this.topics;
        }
        const firstSubtopic = topics.find((item) => item.parentId === topic.id && item.routes);
        if (!firstSubtopic) {
            throw new Error("No subtopic for id "+topic.id+" found");
        }

        return firstSubtopic;
    }

    /**
     * Return process configuration
     * Returns process configuration marked as 'isCurrent==true' if no version is passed
     *
     * @param type
     * @param version
     */
    getProcessConfig(type: string, version?: number): ProcessConfiguration | undefined {
        if (!version) {
            return this.processes.find((config: ProcessConfiguration) => {
                return config.type === type && config.isCurrent;
            });
        } else {
            return this.processes.find((config: ProcessConfiguration) => {
                return config.type === type && config.version === version;
            });
        }
    }
}

